import { z } from 'zod'
import { PRODUCT_CATEGORY_IDS } from '../constants'

/**
 * 골든셋 = 실제로 팔리고 있는 소규모 식품 제품 예시 모음 (기준 데이터).
 * Perplexity(웹 검색) 로 찾거나 어드민이 직접 넣는다. 들어온 건 전부 기준 데이터로 쓰고, 틀린 건 지운다.
 * AI 출력과 같은 모양일 필요는 없다 — 겹치는 것(판매가 · 용량 · 재료 단가 · 재료 구성)만 생성·평가에 쓴다.
 */
export const GOLDEN_SOURCE = ['perplexity', 'manual'] as const
export type GoldenSource = (typeof GOLDEN_SOURCE)[number]

/** 골든셋 시장. kr = 한국 제품(KRW), nyc = NYC 제품(USD). 시뮬레이션의 market(kr/us)과 대응 */
export const GOLDEN_MARKETS = ['kr', 'nyc'] as const
export type GoldenMarket = (typeof GOLDEN_MARKETS)[number]
export function goldenCurrency(market: GoldenMarket): 'KRW' | 'USD' {
  return market === 'kr' ? 'KRW' : 'USD'
}

/** 재료 단가 단위 */
export const PRICE_UNITS = ['kg', 'g', 'L', 'ml', 'ea'] as const
export type PriceUnit = (typeof PRICE_UNITS)[number]

/** 재료 하나: 이름 + 시장 단가 (예: 꿀 $9.50/kg · 꿀 12,000원/kg). 통화는 제품의 market 을 따른다. 양은 없다 */
export const goldenIngredientSchema = z.object({
  name: z.string().trim().min(1).max(80),
  unit: z.enum(PRICE_UNITS).default('kg'),
  unitPrice: z.number().positive().max(1e8).nullable().default(null),
})
export type GoldenIngredient = z.infer<typeof goldenIngredientSchema>

/** 어드민이 직접 입력/수정하는 필드 */
export const goldenProductInputSchema = z.object({
  categoryId: z.enum(PRODUCT_CATEGORY_IDS),
  market: z.enum(GOLDEN_MARKETS).default('nyc'),
  productName: z.string().trim().min(1).max(160),
  brand: z.string().trim().min(1).max(120),
  /** 제품 페이지나 소개 기사 */
  sourceUrl: z.url().max(500).nullable().default(null),
  /** 판매가. 통화는 market 기준 */
  retailPrice: z.number().positive().max(1e8).nullable().default(null),
  /** 판매 단위, 예: "8 oz jar", "6-pack" */
  packSize: z.string().trim().max(80).default(''),
  ingredients: z.array(goldenIngredientSchema).max(40).default([]),
  /** 한 줄 포지셔닝 — 왜 팔리는지 (생성 다양성 참고용) */
  positioning: z.string().trim().max(400).default(''),
})
export type GoldenProductInput = z.infer<typeof goldenProductInputSchema>

export const goldenProductPatchSchema = goldenProductInputSchema.partial()
export type GoldenProductPatch = z.infer<typeof goldenProductPatchSchema>

/** API 응답 (D1 행) */
export const goldenProductSchema = goldenProductInputSchema.extend({
  id: z.string(),
  source: z.enum(GOLDEN_SOURCE),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type GoldenProduct = z.infer<typeof goldenProductSchema>

export const goldenSetListQuerySchema = z.object({
  categoryId: z.enum(PRODUCT_CATEGORY_IDS).optional(),
  market: z.enum(GOLDEN_MARKETS).optional(),
})

/** Perplexity 로 후보 찾기 요청 */
export const goldenSetGenerateSchema = z.object({
  categoryId: z.enum(PRODUCT_CATEGORY_IDS),
  market: z.enum(GOLDEN_MARKETS).default('nyc'),
  count: z.number().int().min(1).max(10).default(5),
  /** 검색 방향 힌트, 예: "vegan", "Korean-owned", "under $10" */
  hint: z.string().trim().max(300).optional(),
})
export type GoldenSetGenerateInput = z.infer<typeof goldenSetGenerateSchema>

const toNumberOrNull = (v: unknown) => {
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null
  if (typeof v === 'string') {
    const n = Number.parseFloat(v.replace(/[^0-9.]/g, ''))
    return Number.isFinite(n) && n > 0 ? n : null
  }
  return null
}
const toUnit = (v: unknown): PriceUnit => {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : ''
  if (s === 'g' || s === 'gram' || s === 'grams') return 'g'
  if (s === 'l' || s === 'liter' || s === 'litre') return 'L'
  if (s === 'ml') return 'ml'
  if (s === 'ea' || s === 'each' || s === 'piece' || s === 'unit') return 'ea'
  return 'kg'
}
const toStringOrEmpty = (v: unknown) => (typeof v === 'string' ? v : '')
/** "honey, chili" · ["honey"] · [{name, unit, unitPriceUsd}] 전부 받아서 정규화 */
const toIngredientList = (v: unknown): unknown[] => {
  const raw = Array.isArray(v)
    ? v
    : typeof v === 'string'
      ? v
          .split(/[,;\n]/)
          .map((s) => s.trim())
          .filter(Boolean)
      : []
  return raw
    .map((item) => {
      if (typeof item === 'string') return { name: item }
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>
        return {
          name: o['name'] ?? o['ingredient'],
          unit: toUnit(o['unit']),
          unitPrice: toNumberOrNull(
            o['unitPrice'] ??
              o['unitPriceUsd'] ??
              o['unitPriceKrw'] ??
              o['pricePerUnit'] ??
              o['price'],
          ),
        }
      }
      return null
    })
    .filter((x) => x !== null && typeof (x as { name?: unknown }).name === 'string')
}

/** 모델(Perplexity) 응답 파싱용 — 느슨하게 받아서 정규화 */
export const goldenCandidateSchema = z.object({
  productName: z.string().trim().min(1).max(160),
  brand: z.string().trim().min(1).max(120),
  sourceUrl: z.preprocess(
    (v) => (typeof v === 'string' && /^https?:\/\//.test(v) ? v : null),
    z.url().max(500).nullable(),
  ),
  retailPrice: z.preprocess(
    (v: unknown) => toNumberOrNull(v ?? null),
    z.number().positive().max(1e8).nullable(),
  ),
  packSize: z.preprocess(toStringOrEmpty, z.string().trim().max(80)),
  ingredients: z.preprocess(toIngredientList, z.array(goldenIngredientSchema).max(40)),
  positioning: z.preprocess(toStringOrEmpty, z.string().trim().max(400)),
})
export type GoldenCandidate = z.infer<typeof goldenCandidateSchema>
/** 항목 하나가 깨져도(이름 null 등) 나머지는 살린다 */
export const goldenCandidateListSchema = z.object({
  products: z.preprocess(
    (v) =>
      Array.isArray(v) ? v.filter((item) => goldenCandidateSchema.safeParse(item).success) : [],
    z.array(goldenCandidateSchema),
  ),
})

/**
 * 제품별 비교 제품 검색 결과 (Perplexity, 2026-09-19 결정). 골든셋은 파이프라인 카테고리 단위라 제품과 안 맞는 경우가
 * 많아, 결과가 저장될 때 제품명·유형으로 같은 종류의 실제 판매 제품을 찾아 internal.competitors 에 붙인다
 */
export const competitorCandidateSchema = z.object({
  productName: z.string().trim().min(1).max(160),
  brand: z.preprocess(toStringOrEmpty, z.string().trim().max(120)),
  sourceUrl: z.preprocess(
    (v) => (typeof v === 'string' && /^https?:\/\//.test(v) ? v : null),
    z.url().max(500).nullable(),
  ),
  retailPrice: z.preprocess(
    (v: unknown) => toNumberOrNull(v ?? null),
    z.number().positive().max(1e8).nullable(),
  ),
  packSize: z.preprocess(toStringOrEmpty, z.string().trim().max(80)),
  positioning: z.preprocess(toStringOrEmpty, z.string().trim().max(300)),
})
export const competitorCandidateListSchema = z.object({
  products: z.array(competitorCandidateSchema).max(12),
})
export type CompetitorCandidate = z.infer<typeof competitorCandidateSchema>
